/**
 * x-fit-rows="fitRows = $rows": how many rows of the page grid (RowGrid) the element's content needs,
 * handed to the expression as $rows whenever that number changes.
 *
 * Every Site Analytics card spans the rows it reports (site-analytics/card.blade.php):
 *  - a card sized by its content measures its body, whose height is its content's;
 *  - a chart card measures its box (site-analytics/chart.blade.php), which fills the card. What it
 *    needs is its padding and every part at its own height, with the chart, the child marked
 *    `data-fit-grow`, counted at its min-height: the chart height its reader set. The chart then
 *    grows into what the rows leave over, so it is never drawn shorter than that.
 *
 * It re-measures on every change of size of the element or of one of its children: the figures
 * arriving, a legend filling in, a row of Explore opening, a new width reflowing the tiles.
 *
 * The row and the gap are read off the grid the element sits in (its grid-auto-rows and row-gap), so
 * the grid's classes stay the one statement of them.
 *
 * It measures in the next animation frame, never in the observer's callback: a new number of rows
 * resizes the very box being observed, and a change of layout inside the callback is the loop WebKit
 * reports as an error on the window ("ResizeObserver loop completed with undelivered
 * notifications"), which Flare would record on every page. Reports within one frame make one fit.
 *
 * A box marked `data-fit-level="<group>"` levels with its row: it is given the most rows any box of
 * its group starting on the same grid row needs, so the boxes of a row end together (Ola's QA, 9 Oct:
 * the key figure whose "Write something new →" wrapped stood a row taller than the five beside it).
 * Only a box with a growing child may level, since its need is read from its parts, never from the
 * height its rows stretched it to, which would only ever grow. Which boxes share a row is read off
 * their cards' offsetTop, which a stagger's transform does not move. When a box's own need changes,
 * the others of its group in that grid fit again (levelled), so one wrapping line lifts its whole row
 * and its unwrapping lets it down.
 */
export default function FitRowsDirective(el, { expression }, { evaluateLater, cleanup }) {
    const assign = evaluateLater(expression);
    let fitted = null;
    let frame = null;
    // The rows this box's own content needs, before levelling with its row.
    let needed = null;

    const fit = () => {
        const grid = el.closest('[data-site-analytics-grid]');

        if (!grid) return;

        const style = window.getComputedStyle(grid);
        const row = parseFloat(style.gridAutoRows);
        const gap = parseFloat(style.rowGap) || 0;
        const own = rowsToFit(neededHeight(el), row, gap);

        if (own === null) return;

        const level = el.dataset?.fitLevel || null;
        const rows = level === null
            ? own
            : Math.max(own, ...rowPeers(grid, el, level).map((peer) => rowsToFit(neededHeight(peer), row, gap) ?? 0));
        const isNeedChanged = own !== needed;

        needed = own;

        if (rows !== fitted) {
            fitted = rows;
            assign(() => {}, { scope: { $rows: rows } });
        }

        if (level !== null && isNeedChanged) levelled(grid).forEach((peer) => peer !== member && peer.level === level && peer.schedule());
    };

    const schedule = () => {
        if (frame !== null) return;

        frame = window.requestAnimationFrame(() => {
            frame = null;
            fit();
        });
    };

    const member = { level: el.dataset?.fitLevel || null, schedule };
    const grid = member.level === null ? null : el.closest('[data-site-analytics-grid]');

    if (grid) levelled(grid).add(member);

    const observer = new ResizeObserver(schedule);

    observer.observe(el);
    [...el.children].forEach((child) => observer.observe(child));
    cleanup(() => {
        observer.disconnect();

        if (grid) levelled(grid).delete(member);

        if (frame !== null) window.cancelAnimationFrame(frame);
    });
}

/** Every levelling box's fit, per grid, so one whose need changed can ask the rest of its group to fit again. */
const levelledByGrid = new WeakMap();

function levelled(grid) {
    if (!levelledByGrid.has(grid)) levelledByGrid.set(grid, new Set());

    return levelledByGrid.get(grid);
}

/** The grid's child holding an element: the card whose rows the element sizes. */
function cardOf(el, grid) {
    let node = el;

    while (node && node.parentElement !== grid) node = node.parentElement;

    return node ?? null;
}

/**
 * The other boxes of a group whose cards start on the same grid row as this one's: the cards of one
 * row share their top edge, so their offsetTop, which no transform moves.
 */
export function rowPeers(grid, el, level) {
    const top = cardOf(el, grid)?.offsetTop;

    if (top === undefined) return [];

    return [...grid.querySelectorAll(`[data-fit-level="${level}"]`)]
        .filter((peer) => peer !== el && cardOf(peer, grid)?.offsetTop === top);
}

/**
 * The height the element's content needs. Without a growing child that is simply its height. With
 * one, the element is stretched to its rows and its own height says nothing, so its parts are added
 * up instead: padding, borders, and each child in the flow with its margins, the growing child at its
 * min-height. The element is a flex column, whose children's margins never collapse.
 */
export function neededHeight(el) {
    const grower = [...el.children].find((child) => child.hasAttribute('data-fit-grow'));

    if (!grower) return el.getBoundingClientRect().height;

    const px = (value) => parseFloat(value) || 0;
    const own = window.getComputedStyle(el);
    let height = px(own.paddingTop) + px(own.paddingBottom) + px(own.borderTopWidth) + px(own.borderBottomWidth);

    for (const child of el.children) {
        const style = window.getComputedStyle(child);

        if (style.display === 'none' || style.position === 'absolute' || style.position === 'fixed') continue;

        height += (child === grower ? px(style.minHeight) : child.getBoundingClientRect().height) + px(style.marginTop) + px(style.marginBottom);
    }

    return height;
}

/**
 * The fewest rows whose box holds `height`: n rows are n * row + (n - 1) * gap tall. Half a pixel of
 * slack, so a body whose height rounds to exactly n rows is not given another for a subpixel. Null
 * while the grid has no row size to measure against (it is not laid out).
 */
export function rowsToFit(height, row, gap) {
    if (!(row > 0)) return null;

    return Math.max(1, Math.ceil((height + gap - 0.5) / (row + gap)));
}
